import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Table, Tag, Button, Card, Select, Space, Typography,
  Tooltip, message, Alert, Statistic, Row, Col, Progress, Input, Switch,
} from 'antd';
import {
  SyncOutlined, DownloadOutlined, FilePdfOutlined,
  CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getAgentAuthHeaders } from '../../services/api/settingsApi';

const { Text } = Typography;
const AGENT_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

// ── API helpers ──────────────────────────────────────────────────────────────

async function fetchDirectorySyncResults(classification) {
  const params = new URLSearchParams();
  if (classification && classification !== 'ALL') params.set('classification', classification);
  const qs = params.toString();
  const url = `${AGENT_URL}/directory-sync/results${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers: getAgentAuthHeaders() });
  if (response.status === 404) {
    // "No completed scans yet" — legitimate empty state, not an error.
    return { scan_run: null, results: [], total: 0 };
  }
  if (!response.ok) {
    const err = new Error(`Directory Sync results request failed (${response.status})`);
    err.response = { status: response.status, data: await response.json().catch(() => ({})) };
    throw err;
  }
  return response.json();
}

async function triggerDirectorySyncScan() {
  const response = await fetch(`${AGENT_URL}/directory-sync/trigger`, {
    method: 'POST',
    headers: getAgentAuthHeaders(),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(`Trigger Directory Sync scan failed (${response.status})`);
    err.response = { status: response.status, data };
    throw err;
  }
  return response.json();
}

async function fetchDirectorySyncActiveRun() {
  const response = await fetch(`${AGENT_URL}/directory-sync/runs/active`, {
    headers: getAgentAuthHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return response.json();
}

// ── Visual config ────────────────────────────────────────────────────────────

const CLASSIFICATION_PALETTE = {
  CLOUD_ONLY: { color: 'var(--odin-text-secondary)',         label: 'CLOUD ONLY' },
  HYBRID:     { color: 'var(--odin-status-completed-text)', label: 'HYBRID' },
  UNKNOWN:    { color: 'var(--odin-status-awaiting-text)',  label: 'UNKNOWN' },
};

function ClassificationBadge({ value }) {
  const cfg = CLASSIFICATION_PALETTE[value] || CLASSIFICATION_PALETTE.UNKNOWN;
  return (
    <Tag style={{
      background: `color-mix(in srgb, ${cfg.color} 16%, transparent)`, color: cfg.color,
      border: `1px solid color-mix(in srgb, ${cfg.color} 40%, transparent)`,
      fontWeight: 700, fontSize: 10, letterSpacing: '0.04em',
    }}>
      {cfg.label}
    </Tag>
  );
}

function parseArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return []; }
  }
  return [];
}

function syncAgeColor(hours) {
  if (hours === null || hours === undefined) return 'var(--odin-text-tertiary)';
  if (hours < 6) return 'var(--odin-text-primary)';
  if (hours < 24) return 'var(--odin-warning)';
  return 'var(--odin-error)';
}

function SyncIndicator({ value }) {
  if (value === null || value === undefined) {
    return (
      <Tooltip title="Field absent in Graph response">
        <QuestionCircleOutlined style={{ color: 'var(--odin-warning)' }} />
      </Tooltip>
    );
  }
  return value
    ? <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
    : <CloseCircleOutlined style={{ color: 'var(--odin-text-secondary)' }} />;
}

const COOLDOWN_MS = 30 * 60 * 1000;
function isCoolingDown(lastScanAt) {
  if (!lastScanAt) return false;
  return Date.now() - new Date(lastScanAt).getTime() < COOLDOWN_MS;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function DirectorySyncReport() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = hasRole('Admin');
  const [classificationFilter, setClassificationFilter] = useState('ALL');
  const [hybridOnly, setHybridOnly] = useState(false);
  const [search, setSearch] = useState('');

  const effectiveFilter = hybridOnly ? 'HYBRID' : classificationFilter;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['directory-sync-results', effectiveFilter],
    queryFn: () => fetchDirectorySyncResults(effectiveFilter),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: activeRun } = useQuery({
    queryKey: ['directory-sync-active'],
    queryFn: fetchDirectorySyncActiveRun,
    refetchInterval: (q) => (q.state.data?.id ? 5000 : 60000),
    staleTime: 0,
    initialData: null,
  });

  const prevActiveId = useRef(undefined);
  useEffect(() => {
    if (prevActiveId.current !== undefined && prevActiveId.current !== null && !activeRun?.id) {
      // Invalidate ALL filter variants, not just the active query — each
      // filter value is its own cache entry, and a stale empty pre-scan
      // entry under one filter will show as "no data" after the scan
      // completes if we only refetch the currently active filter.
      queryClient.invalidateQueries({ queryKey: ['directory-sync-results'] });
      message.success('Directory Sync scan complete — results updated.');
    }
    prevActiveId.current = activeRun?.id ?? null;
  }, [activeRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerMutation = useMutation({
    mutationFn: triggerDirectorySyncScan,
    onSuccess: () => message.success('Scan triggered. Results will update in ~1 minute.'),
    onError: (err) => {
      const status = err.response?.status;
      if (status === 429) {
        const retry = err.response?.data?.detail?.retryAfterSeconds ?? 0;
        message.warning(`Cooldown active — retry in ${Math.ceil(retry / 60)} minute(s).`);
      } else {
        message.error(`Trigger failed: ${err.message}`);
      }
    },
  });

  const scanRun = data?.scan_run;
  const results = data?.results || [];
  const filtered = results.filter((r) =>
    !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase()),
  );

  const counts = {
    CLOUD_ONLY: scanRun?.cloud_only_count ?? 0,
    HYBRID: scanRun?.hybrid_count ?? 0,
    UNKNOWN: scanRun?.unknown_count ?? 0,
  };

  const cooldown = isCoolingDown(scanRun?.completed_at || scanRun?.started_at);

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      render: (v) => <Text strong style={{ color: 'var(--odin-text-primary)' }}>{v}</Text>,
      sorter: (a, b) => (a.tenant_name || '').localeCompare(b.tenant_name || ''),
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 170,
      render: (v) => <ClassificationBadge value={v} />,
      filters: Object.keys(CLASSIFICATION_PALETTE).map((k) => ({ text: k, value: k })),
      onFilter: (value, record) => record.classification === value,
    },
    {
      title: 'Sync Enabled',
      dataIndex: 'on_premises_sync_enabled',
      key: 'sync_enabled',
      width: 110,
      align: 'center',
      render: (v) => <SyncIndicator value={v} />,
    },
    {
      title: 'Last Sync',
      dataIndex: 'on_premises_last_sync_at',
      key: 'last_sync',
      width: 170,
      render: (v) => v ? new Date(v).toLocaleString() : <Text type="secondary">—</Text>,
    },
    {
      title: 'Sync Age',
      dataIndex: 'sync_age_hours',
      key: 'sync_age_hours',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.sync_age_hours ?? 0) - (b.sync_age_hours ?? 0),
      render: (v) => v === null || v === undefined
        ? <Text type="secondary">—</Text>
        : <Tooltip title="Entra Connect sync age (h)">
            <span style={{ color: syncAgeColor(v) }}>{v.toFixed(1)} h</span>
          </Tooltip>,
    },
    {
      title: 'Federation',
      dataIndex: 'federated_domain_count',
      key: 'federation',
      width: 130,
      render: (count, record) => {
        if (!count) return <Text type="secondary">—</Text>;
        const domains = parseArray(record.federated_domains);
        return (
          <Tooltip title={domains.map((d) => d.name).join(', ')}>
            <Tag color="purple">ADFS · {count}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="Users sourced from on-prem AD — must be password-reset on the DC, not in M365">
          <span>Synced Users</span>
        </Tooltip>
      ),
      dataIndex: 'synced_user_count',
      key: 'synced_user_count',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.synced_user_count ?? 0) - (b.synced_user_count ?? 0),
      render: (v) => v === null || v === undefined
        ? <Text type="secondary">—</Text>
        : <span>{v}</span>,
    },
    {
      title: 'Cloud Users',
      dataIndex: 'cloud_user_count',
      key: 'cloud_user_count',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.cloud_user_count ?? 0) - (b.cloud_user_count ?? 0),
      render: (v) => v === null || v === undefined
        ? <Text type="secondary">—</Text>
        : <span>{v}</span>,
    },
    {
      title: (
        <Tooltip title="Devices enrolled in Intune MDM — green check when present, indicates cloud-managed device posture">
          <span>Intune Devices</span>
        </Tooltip>
      ),
      dataIndex: 'intune_managed_device_count',
      key: 'intune_managed_device_count',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.intune_managed_device_count ?? 0) - (b.intune_managed_device_count ?? 0),
      render: (v) => {
        if (v === null || v === undefined) return <Text type="secondary">—</Text>;
        if (v === 0) return <Text type="secondary">0</Text>;
        return (
          <Space size={4}>
            <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
            <span>{v}</span>
          </Space>
        );
      },
    },
    {
      title: 'Default Domain',
      dataIndex: 'default_domain',
      key: 'default_domain',
      ellipsis: true,
      render: (v) => <Text style={{ color: 'var(--odin-text-secondary)', fontFamily: 'monospace' }}>{v}</Text>,
    },
  ];

  if (isError) {
    return (
      <Alert type="error" message="Failed to load Directory Sync results"
             description={error?.message || 'Unknown error'} showIcon />
    );
  }

  const noScanYet = !isPending && !scanRun;

  return (
    <div>
      {noScanYet && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="No Directory Sync scan has run yet"
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>
                {isAdmin
                  ? 'Click below to trigger your first scan. It takes ~30 seconds for the current estate.'
                  : 'Ask an admin to trigger the first scan, or contact your CSG account manager.'}
              </Text>
              {isAdmin && (
                <Button
                  type="primary"
                  icon={<SyncOutlined spin={triggerMutation.isPending} />}
                  loading={triggerMutation.isPending}
                  onClick={() => triggerMutation.mutate()}
                  disabled={triggerMutation.isPending}
                >
                  Start First Scan
                </Button>
              )}
            </Space>
          }
        />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="Cloud-only" value={counts.CLOUD_ONLY}
            valueStyle={{ color: CLASSIFICATION_PALETTE.CLOUD_ONLY.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="Hybrid (Agent Smith)" value={counts.HYBRID}
            valueStyle={{ color: CLASSIFICATION_PALETTE.HYBRID.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="Unknown" value={counts.UNKNOWN}
            valueStyle={{ color: CLASSIFICATION_PALETTE.UNKNOWN.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="Total" value={scanRun?.tenant_count ?? 0} /></Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={classificationFilter}
            disabled={hybridOnly}
            onChange={setClassificationFilter}
            style={{ width: 200 }}
            options={[
              { value: 'ALL', label: 'All classifications' },
              { value: 'CLOUD_ONLY', label: 'Cloud-only' },
              { value: 'HYBRID', label: 'Hybrid' },
              { value: 'UNKNOWN', label: 'Unknown' },
            ]}
          />
          <Switch
            checked={hybridOnly}
            onChange={setHybridOnly}
            checkedChildren="Hybrid only"
            unCheckedChildren="Hybrid only"
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {isAdmin && (
            <Tooltip title={cooldown ? '30-minute cooldown active' : 'Run a fresh scan'}>
              <Button
                icon={<SyncOutlined spin={triggerMutation.isPending || !!activeRun?.id} />}
                onClick={() => triggerMutation.mutate()}
                disabled={cooldown || triggerMutation.isPending || !!activeRun?.id}
              >
                Refresh Now
              </Button>
            </Tooltip>
          )}
          <Button icon={<DownloadOutlined />} href={`${AGENT_URL}/directory-sync/export/csv`}>
            Export CSV
          </Button>
          <Button icon={<FilePdfOutlined />} href={`${AGENT_URL}/directory-sync/export/pdf`}>
            Export PDF
          </Button>
          {scanRun?.completed_at && (
            <Text type="secondary">
              Last scan: {new Date(scanRun.completed_at).toLocaleString()}
            </Text>
          )}
        </Space>
      </Card>

      {activeRun?.id && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`Scan in progress — ${activeRun.scanned_so_far ?? 0} / ${activeRun.total_expected ?? '?'} tenants`}
          description={<Progress
            percent={
              activeRun.total_expected
                ? Math.round(((activeRun.scanned_so_far || 0) / activeRun.total_expected) * 100)
                : 0
            }
            status="active"
            strokeColor="var(--odin-accent)"
          />}
        />
      )}

      <Table
        size="small"
        loading={isPending}
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 50, showSizeChanger: true }}
      />
    </div>
  );
}
